import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import axios from "axios";
import { toast } from "sonner";
import { loginRequest } from "../config/msalConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState(localStorage.getItem("authMethod") || null);
  const [authError, setAuthError] = useState("");

  const clearAppSession = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("authMethod");
    setToken(null);
    setAuthMethod(null);
    setUser(null);
    setAuthError("");
    delete axios.defaults.headers.common["Authorization"];
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setAuthError("");
    } catch {
      clearAppSession();
    } finally {
      setLoading(false);
    }
  }, [clearAppSession]);

  useEffect(() => {
    instance.handleRedirectPromise()
      .then((response) => {
        if (response) console.log("Redirect response received");
      })
      .catch((error) => console.error("Redirect error:", error));
  }, [instance]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchCurrentUser();
    } else if (!isMsalAuthenticated && inProgress === InteractionStatus.None) {
      setLoading(false);
    }
  }, [token, isMsalAuthenticated, inProgress, fetchCurrentUser]);

  const saveAuthenticatedUser = useCallback((data) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("authMethod", "microsoft");
    setToken(data.token);
    setAuthMethod("microsoft");
    setUser(data.user);
    setAuthError("");
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
  }, []);

  const handleMicrosoftAuth = useCallback(async () => {
    if (inProgress !== InteractionStatus.None || accounts.length === 0) return;

    const exchange = async (forceRefresh) => {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        forceRefresh,
      });
      return axios.post(`${API}/auth/microsoft`, {
        access_token: tokenResponse.accessToken,
        id_token: tokenResponse.idToken,
      });
    };

    try {
      setLoading(true);
      setAuthError("");
      let response;
      try {
        response = await exchange(false);
      } catch (error) {
        const status = error.response?.status;
        if (status !== 401 && status !== 403) throw error;
        console.warn("Microsoft role exchange rejected cached token; forcing Entra token refresh.");
        response = await exchange(true);
      }
      saveAuthenticatedUser(response.data);
    } catch (error) {
      console.error("Microsoft auth error:", error);
      const detail = error.response?.data?.detail || error.message || "Microsoft sign-in failed";
      setAuthError(detail);
      toast.error(detail);

      if (error.name === "InteractionRequiredAuthError") {
        try {
          const tokenResponse = await instance.acquireTokenPopup({
            ...loginRequest,
            prompt: "select_account",
          });
          const response = await axios.post(`${API}/auth/microsoft`, {
            access_token: tokenResponse.accessToken,
            id_token: tokenResponse.idToken,
          });
          saveAuthenticatedUser(response.data);
        } catch (popupError) {
          console.error("Popup auth error:", popupError);
          const popupDetail = popupError.response?.data?.detail || popupError.message || "Microsoft sign-in failed";
          setAuthError(popupDetail);
          toast.error(popupDetail);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, inProgress, saveAuthenticatedUser]);

  useEffect(() => {
    if (isMsalAuthenticated && accounts.length > 0 && inProgress === InteractionStatus.None) {
      handleMicrosoftAuth();
    }
  }, [isMsalAuthenticated, accounts, inProgress, handleMicrosoftAuth]);

  const login = async (username, password) => {
    setAuthError("");
    const response = await axios.post(`${API}/auth/login`, { username, password });
    localStorage.setItem("token", response.data.token);
    localStorage.setItem("authMethod", "local");
    setToken(response.data.token);
    setAuthMethod("local");
    setUser(response.data.user);
    axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`;
    return response.data;
  };

  const loginWithMicrosoft = async () => {
    if (inProgress !== InteractionStatus.None) return;
    try {
      setLoading(true);
      setAuthError("");
      await instance.loginRedirect({
        ...loginRequest,
        prompt: "select_account",
      });
    } catch (error) {
      console.error("Microsoft login error:", error);
      if (error.errorCode === "interaction_in_progress") {
        sessionStorage.clear();
        window.location.reload();
        return;
      }
      const detail = error.message || "Microsoft sign-in failed";
      setAuthError(detail);
      toast.error(detail);
      setLoading(false);
      throw error;
    }
  };

  const logout = useCallback(async () => {
    const microsoftAccount = accounts[0] || null;
    clearAppSession();

    if (microsoftAccount) {
      try {
        await instance.logoutRedirect({
          account: microsoftAccount,
          postLogoutRedirectUri: window.location.origin + "/login",
        });
      } catch (error) {
        console.error("Microsoft logout error:", error);
        window.location.href = "/login";
      }
    }
  }, [accounts, clearAppSession, instance]);

  const role = user?.role || "sales_viewer";
  const isAdmin = role === "graphics_admin";
  const isManager = role === "sales_manager";
  const isSalesRep = role === "sales_rep";
  const isViewer = role === "sales_viewer";
  const canViewAllProjects = Boolean(user);
  const canCreateProject = isAdmin || isManager || isSalesRep;
  const canAccessIntakeQueue = isAdmin || isManager || isSalesRep;
  const canChangeProjectStatus = canCreateProject;
  const canFullProjectEdit = isAdmin || isManager;
  const roleLabel = {
    graphics_admin: "Graphics Admin",
    sales_manager: "Sales Ops Management",
    sales_rep: "Sales Ops",
    sales_viewer: "Viewer",
  }[role] || role;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        loginWithMicrosoft,
        logout,
        loading,
        authError,
        role,
        roleLabel,
        isAdmin,
        isManager,
        isSalesRep,
        isViewer,
        canViewAllProjects,
        canCreateProject,
        canAccessIntakeQueue,
        canChangeProjectStatus,
        canFullProjectEdit,
        authMethod,
        isMsalAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
